import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ArrowUp } from 'lucide-react';
import { Square } from '@phosphor-icons/react';

export interface ChatInputChip {
  type?: 'file' | 'video' | 'ocr' | 'custom';
  label?: string;
  icon?: React.ReactNode;
  onRemove?: () => void;
  onClick?: () => void;
  element?: React.ReactNode; // For fully custom chips (when provided, type and label are optional)
}

export interface ChatInputProps {
  // Input value and handlers
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  disabled?: boolean;
  disableSend?: boolean; // Additional condition to disable send button

  // Loading state
  isLoading?: boolean;
  loadingIcon?: 'square' | 'spinner';
  onStopLoading?: () => void;

  // Feature chips
  chips?: ChatInputChip[];

  // Left button configuration
  leftButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    ariaLabel: string;
  };

  // Focus state
  isFocused?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;

  // Behavior
  maxHeight?: number;
  multilineThreshold?: number;
  onKeyPress?: (event: React.KeyboardEvent) => void;

  // Styling
  containerClassName?: string;
  inactiveOpacity?: boolean; // Reduce opacity when inactive

  // Extra content (dropdowns, menus, etc.)
  children?: React.ReactNode;

  // Input ref (optional external ref for the textarea)
  inputRef?: React.RefObject<HTMLTextAreaElement>;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  disableSend = false,
  isLoading = false,
  loadingIcon = 'square',
  onStopLoading,
  chips = [],
  leftButton,
  isFocused: externalFocused,
  onFocus,
  onBlur,
  maxHeight = 150,
  multilineThreshold = 40,
  onKeyPress,
  containerClassName = '',
  inactiveOpacity = false,
  children,
  inputRef: externalInputRef,
}) => {
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [internalFocused, setInternalFocused] = React.useState(false);
  const [isMultiline, setIsMultiline] = React.useState(false);

  // Use external ref if provided, otherwise use internal ref
  const textareaRef = externalInputRef || internalTextareaRef;

  const isFocused = externalFocused ?? internalFocused;
  const hasChips = chips.length > 0;

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    // Auto-resize
    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, maxHeight);
    e.target.style.height = `${newHeight}px`;
    setIsMultiline(newHeight > multilineThreshold);
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setInternalFocused(true);
    onFocus?.();
    // Ensure proper height on focus
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
  };

  const handleBlur = () => {
    setInternalFocused(false);
    onBlur?.();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled && !isLoading && !disableSend) {
      onSubmit();
      // Reset textarea height after submit
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        setIsMultiline(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without shift = submit
    // Shift+Enter = new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled && !isLoading && !disableSend) {
        onSubmit();
        // Reset textarea height after submit
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          setIsMultiline(false);
        }
      }
    }
  };

  return (
    <div className={cn('px-3 pb-3', containerClassName)}>
      <div className="max-w-3xl mx-auto w-full">
        <form onSubmit={handleSubmit} className="w-full">
          <div
            className={cn(
              'bg-white dark:bg-background border shadow-sm',
              'transition-all duration-300 ease-in-out',
              'rounded-[20px]',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
              // Adjust layout when chips are present
              hasChips
                ? 'p-1.5 flex flex-col gap-1.5'
                : 'px-1.5 py-1 flex items-end gap-1',
            )}
          >
            {/* Textarea - shows above when chips are present */}
            {hasChips && (
              <div className={cn('w-full px-1 py-1.5')}>
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'w-full min-h-[24px] max-h-[150px] resize-none border-0 px-2 py-1',
                    'text-sm leading-6',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                    inactiveOpacity
                      ? 'placeholder:text-muted-foreground/50'
                      : 'placeholder:text-muted-foreground',
                  )}
                  placeholder={placeholder}
                  aria-label="chat input"
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyPress={onKeyPress}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  rows={1}
                />
              </div>
            )}

            {/* Main row with left button, input/chips, and send button */}
            <div
              className={cn(
                'flex gap-1 w-full',
                isMultiline ? 'items-end pb-0.5' : 'items-center',
              )}
            >
              {/* Left button (Settings/Plus) */}
              {leftButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={leftButton.onClick}
                  className="h-7 w-7 rounded-full hover:bg-accent flex-shrink-0"
                  aria-label={leftButton.ariaLabel}
                >
                  {leftButton.icon}
                </Button>
              )}

              {/* Middle - Either textarea or chips */}
              {!hasChips && (
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'flex-1 min-h-[24px] max-h-[150px] resize-none border-0 pl-1 pr-2 py-1',
                    'text-sm leading-6',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                    inactiveOpacity
                      ? 'placeholder:text-muted-foreground/50'
                      : 'placeholder:text-muted-foreground',
                  )}
                  placeholder={placeholder}
                  aria-label="chat input"
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyPress={onKeyPress}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  rows={1}
                />
              )}

              {/* Chips - shows when present */}
              {hasChips && (
                <div className="flex-1 flex items-center gap-2 flex-wrap">
                  {chips.map((chip, index) => {
                    // If custom element provided, use it
                    if (chip.element) {
                      return (
                        <React.Fragment key={index}>
                          {chip.element}
                        </React.Fragment>
                      );
                    }

                    // Otherwise render default chip
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={chip.onClick}
                        className="group flex items-center gap-2 h-7 bg-primary/10 hover:bg-primary/15 text-primary rounded-full px-3 transition-all animate-in slide-in-from-left-2 fade-in duration-300"
                      >
                        {chip.icon && (
                          <div className="w-4 h-4">{chip.icon}</div>
                        )}
                        <span className="text-xs font-medium">
                          {chip.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Right side - Send/Stop button */}
              <div
                className={cn(
                  'flex gap-1 flex-shrink-0',
                  isMultiline ? 'items-end' : 'items-center',
                )}
              >
                {isLoading ? (
                  <Button
                    type="button"
                    onClick={onStopLoading}
                    size="icon"
                    variant="default"
                    className="h-7 w-7 rounded-full transition-all duration-300"
                  >
                    {loadingIcon === 'square' ? (
                      <Square
                        className="w-3.5 h-3.5 text-white"
                        weight="fill"
                      />
                    ) : (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!value.trim() || disabled || disableSend}
                    variant={
                      !value.trim() || disabled || disableSend
                        ? 'ghost'
                        : 'default'
                    }
                    className={cn(
                      'h-7 w-7 rounded-full transition-all duration-300',
                      (!value.trim() || disabled || disableSend) &&
                        'bg-muted hover:bg-muted/80',
                      value.trim() &&
                        !disabled &&
                        !disableSend &&
                        'bg-primary hover:bg-primary/90 hover:scale-105',
                    )}
                  >
                    <ArrowUp
                      className={cn(
                        'h-4 w-4 transition-all duration-300',
                        !value.trim() || disabled || disableSend
                          ? 'text-muted-foreground'
                          : 'text-primary-foreground',
                        value.trim() &&
                          !disabled &&
                          !disableSend &&
                          'hover:-translate-y-0.5',
                      )}
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* Extra content (dropdowns, menus, etc.) */}
        {children}
      </div>
    </div>
  );
};
